import PasswordPopper from "@mirage/components/passwordPopper/PasswordPopper";
import { ErrorSharp, Visibility, VisibilityOff } from "@mui/icons-material";
import { Alert, Button, Grid, IconButton, InputAdornment, TextField } from "@mui/material";
import { Box, Stack } from "@mui/system";
import { ResultBoardStateSetter } from "@shared/mirage/core/contracts/ResultBoard";
import { ChangePassFieldValidationResult, ChangePasswordRequest, ChangePasswordResult } from "@shared/mirage/core/contracts/User";
import validators, { IPasswordValidatorResult, ITwoPasswordsValidatorResult } from "@shared/mirage/validators";
import React, { memo, useState } from "react";
import { IChangePasswordService } from "@shared/mirage/services/IChangePasswordService";
import { useStableNavigate } from "@mirage/hooks/useStableNavigate";

interface ChangePasswordBoilerplateProps {
    service: IChangePasswordService;
    backButtonRoute: string;
    setResultBoard: ResultBoardStateSetter;
}

const ChangePasswordBoilerplate = memo((props: ChangePasswordBoilerplateProps) => {
    const navigate = useStableNavigate();
    const [oldPassword, setOldPassword] = useState({ value: "", type: "password", showPassword: false, validationResult: { passed: true } } as IChangePasswordInputData);
    const [newPassword, setNewPassword] = useState({ value: "", type: "password", showPassword: false, validationResult: { passed: true } } as IChangePasswordInputData);
    const [confirmNewPassword, setNewPasswordConfirm] = useState({
        value: "",
        type: "password",
        showPassword: false,
        validationResult: { passed: true },
    } as IChangePasswordInputData);

    const [passwordPopperAnchorElement, setPasswordPopperAnchorElement] = React.useState<null | HTMLElement>(null);
    const [passwordPopperVisibility, setPasswordPopperVisibility] = React.useState(false);
    const [alertData, setAlertData] = useState({ show: false, text: "" });

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const closeAlert = () => {
        setAlertData((previousState) => {
            return {
                ...previousState,
                show: false,
            };
        });
    };

    const showPasswordHandler = (reference: ChangePasswordFields, hidePassword?: true) => {
        switch (reference) {
            case "oldPassword":
                setOldPassword((previousState) => {
                    return {
                        ...previousState,
                        showPassword: hidePassword ? false : !previousState.showPassword,
                    };
                });
                break;
            case "newPassword":
                setNewPassword((previousState) => {
                    return {
                        ...previousState,
                        showPassword: hidePassword ? false : !previousState.showPassword,
                    };
                });
                break;
            case "confirmNewPassword":
                setNewPasswordConfirm((previousState) => {
                    return {
                        ...previousState,
                        showPassword: hidePassword ? false : !previousState.showPassword,
                    };
                });
                break;
        }
    };

    const handleInputBlur = (reference: ChangePasswordFields) => {
        const validationResult = validateInput(reference);
        handleInputValidation(reference, validationResult);
    };

    const validateInput = (reference: ChangePasswordFields) => {
        switch (reference) {
            case "oldPassword":
                return validators.IsStringNotEmptyValidator(oldPassword.value, "Enter old password");
            case "newPassword":
                return validators.PasswordValidator(newPassword.value);
            case "confirmNewPassword":
                return validators.CompareTwoPasswords(newPassword.value, confirmNewPassword.value);
            default:
                throw Error(`Bad reference for validation - ${reference}`);
        }
    };

    const handleInputChange = (value: string, reference: ChangePasswordFields) => {
        switch (reference) {
            case "oldPassword":
                setOldPassword((previousState) => {
                    return {
                        ...previousState,
                        value: value,
                    };
                });

                break;
            case "newPassword":
                setNewPassword((previousState) => {
                    const passwordValidationResult: IPasswordValidatorResult = validators.PasswordValidator(value);
                    return {
                        ...previousState,
                        value: value,
                        validationResult: passwordValidationResult,
                    };
                });
                break;
            case "confirmNewPassword":
                setNewPasswordConfirm((previousState) => {
                    const passwordComplianceValidationResult: ITwoPasswordsValidatorResult = validators.CompareTwoPasswords(newPassword.value, value);
                    return {
                        ...previousState,
                        value: value,
                        validationResult: passwordComplianceValidationResult,
                    };
                });
                break;
            default:
                throw Error(`Bad reference for Input Change - ${reference}`);
        }
    };

    const handleInputValidation = (reference: ChangePasswordFields, validationResult: { passed?: boolean; error?: string }) => {
        switch (reference) {
            case "oldPassword":
                setOldPassword((previousState) => {
                    return {
                        ...previousState,
                        validationResult: validationResult,
                    };
                });
                break;
            case "newPassword":
                setNewPassword((previousState) => {
                    return {
                        ...previousState,
                        validationResult: validationResult,
                    };
                });
                break;
            case "confirmNewPassword":
                setNewPasswordConfirm((previousState) => {
                    return {
                        ...previousState,
                        validationResult: validationResult,
                    };
                });
                break;
            default:
                throw Error(`Bad reference for Input Validation - ${reference}`);
        }
    };

    const handleConfirm = async () => {
        const oldPasswordValidation = validateInput("oldPassword");
        const newPasswordValidation = validateInput("newPassword");
        const confirmNewPasswordValidation = validateInput("confirmNewPassword");

        if ((newPasswordValidation.passed && confirmNewPasswordValidation.passed) || oldPasswordValidation.passed) {
            const changePasswordRequest: Omit<ChangePasswordRequest, "userId"> = {
                oldPassword: oldPassword.value,
                newPassword: newPassword.value,
                confirmNewPassword: confirmNewPassword.value,
            };

            const changePasswordRes: ChangePasswordResult = await props.service.changePassword(changePasswordRequest);

            if (changePasswordRes.errorMsg && changePasswordRes?.errorData?.length) {
                changePasswordRes.errorData.forEach((errorData: ChangePassFieldValidationResult) => {
                    handleInputValidation(errorData.fieldName, { passed: false, error: errorData?.errorMsg });
                });
            } else if (changePasswordRes.errorMsg) {
                setAlertData({ show: true, text: changePasswordRes.errorMsg });
            } else {
                handleInputValidation("oldPassword", { passed: true, error: "" });
                handleInputValidation("newPassword", { passed: true, error: "" });
                handleInputValidation("confirmNewPassword", { passed: true, error: "" });
                props.setResultBoard({ show: true, message: "Password changed successfully", success: true, isLoading: false });
            }
        } else {
            handleInputValidation("oldPassword", oldPasswordValidation);
            handleInputValidation("newPassword", newPasswordValidation);
            handleInputValidation("confirmNewPassword", confirmNewPasswordValidation);
        }
    };

    const getPassFieldsEndAdornment = (fieldRef: ChangePasswordFields) => {
        const showPassword = fieldRef === "oldPassword" ? oldPassword.showPassword : fieldRef === "newPassword" ? newPassword.showPassword : confirmNewPassword.showPassword;
        return (
            <InputAdornment position="end">
                <IconButton onClick={() => showPasswordHandler(fieldRef)} onMouseDown={handleMouseDownPassword}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
            </InputAdornment>
        );
    };

    const getErrorMessage = (error: boolean | undefined, errorMessage: string | undefined) => {
        return !error ? (
            <span style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem" }}>
                <ErrorSharp sx={{ width: "0.7em", height: "0.7em" }}></ErrorSharp>
                <span>{errorMessage}</span>
            </span>
        ) : null;
    };

    const getChangePasswordForm = () => {
        /* TODO: Turn off autofill in new password input so it doesn't get on top of popper element 
        https://stackoverflow.com/questions/25823448/ng-form-and-autocomplete-off/39689037#39689037 */
        return (
            <>
                <Stack spacing={4} justifyContent="center" alignItems="center" width="90%" marginLeft="5%">
                    <TextField
                        className="publicPortalField"
                        required={true}
                        id="changePassword-oldPassword"
                        error={!oldPassword.validationResult.passed}
                        defaultValue={oldPassword.value}
                        type={oldPassword.showPassword ? "text" : oldPassword.type}
                        helperText={getErrorMessage(oldPassword.validationResult.passed, oldPassword.validationResult.error)}
                        InputProps={{ endAdornment: getPassFieldsEndAdornment("oldPassword") }}
                        onChange={(e) => handleInputChange(e.target.value, "oldPassword")}
                        label="Old Password"
                        variant="outlined"
                    />
                    {passwordPopperVisibility && passwordPopperAnchorElement && newPassword.validationResult.conditions ? (
                        <PasswordPopper anchorElement={passwordPopperAnchorElement} conditions={newPassword.validationResult.conditions}></PasswordPopper>
                    ) : null}
                    <TextField
                        className="publicPortalField"
                        required={true}
                        id="changePassword-newPassword"
                        error={!newPassword.validationResult.passed}
                        defaultValue={newPassword.value}
                        type={newPassword.showPassword ? "text" : newPassword.type}
                        InputProps={{ endAdornment: getPassFieldsEndAdornment("newPassword") }}
                        onChange={(e) => handleInputChange(e.target.value, "newPassword")}
                        onFocus={(e) => {
                            handleInputBlur("newPassword");
                            const inputContainer = e.currentTarget.parentElement;

                            if (!passwordPopperAnchorElement) {
                                setPasswordPopperAnchorElement(inputContainer);
                            }
                            if (!passwordPopperVisibility) {
                                setPasswordPopperVisibility(true);
                            }
                        }}
                        onBlur={() => setPasswordPopperVisibility(false)}
                        label="New Password"
                        variant="outlined"
                    />
                    <TextField
                        className="publicPortalField"
                        required={true}
                        id="changePassword-confirmNewPassword"
                        error={!confirmNewPassword.validationResult.passed}
                        defaultValue={oldPassword.value}
                        type={confirmNewPassword.showPassword ? "text" : confirmNewPassword.type}
                        helperText={getErrorMessage(confirmNewPassword.validationResult.passed, confirmNewPassword.validationResult.error)}
                        InputProps={{ endAdornment: getPassFieldsEndAdornment("confirmNewPassword") }}
                        onChange={(e) => handleInputChange(e.target.value, "confirmNewPassword")}
                        onBlur={() => handleInputBlur("confirmNewPassword")}
                        label="New Password Confirm"
                        variant="outlined"
                    />
                </Stack>
                <Grid container sx={{ marginTop: "2rem" }} justifyContent="center">
                    {getButtons()}
                </Grid>
            </>
        );
    };

    const getButtons = () => {
        return (
            <>
                <Grid size={{ xs: 3 }} textAlign="center">
                    <Button onClick={() => navigate(props.backButtonRoute)}>Cancel</Button>
                </Grid>
                <Grid size={{ xs: 3 }} textAlign="center">
                    <Button variant="contained" onClick={async () => await handleConfirm()}>
                        Confirm
                    </Button>
                </Grid>
            </>
        );
    };

    return (
        <>
            {getChangePasswordForm()}
            <Box position="absolute" bottom="20px" left="20px" width="40%">
                {alertData.show ? (
                    <Alert severity="error" onClose={closeAlert}>
                        {alertData.text}
                    </Alert>
                ) : null}
            </Box>
        </>
    );
});

export type ChangePasswordFields = keyof Pick<ChangePasswordRequest, "oldPassword" | "newPassword" | "confirmNewPassword">;
export interface IChangePasswordInputData {
    value: string;
    type: "text" | "password";
    showPassword?: boolean;
    validationResult: IPasswordValidatorResult;
}

export default ChangePasswordBoilerplate;
